import * as React from 'react';
import type { AddCustomerContact } from './models/CustomerInterfaces';
import { toast } from 'react-toastify';
import Modal from 'react-bootstrap/Modal';
import { GetQuery, PostQuery } from './Shared';

interface JobDetailsAddContactModalProps {
    customerId: number;
    id: number;
    showModal: boolean;
    hideModal(): void;
    saveModal(newContact: CustomerContactList): void;
}

interface CustomerContactList {
    id: number;
    customerId: number;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    mobile: string;
    emailSubscription: boolean;
}

const JobDetailsAddContactModalData = (props: JobDetailsAddContactModalProps) => {
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [addContact, setAddContact] = React.useState<AddCustomerContact>({
        customerId: props.customerId,
        customerContactId: -1,
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        phone: "",
        emailSubscription: true
    });
    const [saveDisabled, setSaveDisabled] = React.useState(false);

    React.useEffect(() => {
        if (props.id > 0) {
            getData();
        } else {
            setLoading(false);
        }
    }, [props.id]);

    const getData = async () => {
        const apiUrl = 'GetCustomerContactById?Id=' + props.id;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
                setLoading(false);
            } else {
                setAddContact(result.data);
                setLoading(false);
            }
        }
    }

    const hideModal = () => {
        let resetContact = resetAddContact();
        setAddContact(resetContact);
        setErrors({});
        props.hideModal();
    }

    const resetAddContact = () => {
        let resetContact: AddCustomerContact = {
            customerId: props.customerId,
            customerContactId: -1,
            firstName: "",
            lastName: "",
            email: "",
            mobile: "",
            phone: "",
            emailSubscription: true
        };

        return resetContact;
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handleAddContactChange = (e: any) => {
        clearErrors(e.target.name);
        setAddContact(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
        setSaveDisabled(false);
    }

    const validateAddContact = () => {
        let fields = addContact;
        let errorList: any = {};
        let formIsValid = true;

        if (!fields.firstName) {
            formIsValid = false;
            errorList["firstName"] = "First Name is required";
        }

        if (!fields.phone && !fields.mobile) {
            formIsValid = false;
            errorList["phone"] = "Phone or Mobile is required";
        }

        if (!fields.email) {
            formIsValid = false;
            errorList["email"] = "Email is required";
        } else if (fields.email.indexOf("@") < 0) {
            formIsValid = false;
            errorList["email"] = "Valid email is required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const saveAddContact = () => {
        if (saveDisabled) {
            return;
        }

        setSaveDisabled(true);

        if (validateAddContact()) {
            addContactSave();
        }
    }

    const addContactSave = async () => {
        var addEditContact: AddCustomerContact = {
            customerId: props.customerId,
            customerContactId: props.id,
            firstName: addContact.firstName,
            lastName: addContact.lastName,
            email: addContact.email,
            phone: addContact.phone,
            mobile: addContact.mobile,
            emailSubscription: addContact.emailSubscription
        };

        var jsonItem = JSON.stringify(addEditContact);

        const apiUrl = "AddContact";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.messages.length == 0) {
                var newContact: CustomerContactList = {
                    id: result.data.id,
                    customerId: props.customerId,
                    name: result.data.name,
                    firstName: result.data.firstName,
                    lastName: result.data.lastName,
                    email: result.data.email,
                    phone: result.data.phone,
                    mobile: result.data.mobile,
                    emailSubscription: result.data.emailSubscription
                };

                let resetContact = resetAddContact();

                toast.success("Customer Contact Saved");
                setAddContact(resetContact);
                setErrors({});
                props.saveModal(newContact);
            } else {
                for (var i = 0; i < result.data.messages.length; i++) {
                    toast.error(result.data.messages[i].content);
                }
            }
        }
    }

    const renderDetails = (
        <Modal static aria-labelledby="contained-modal-title-sm" show={props.showModal} onHide={hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>{props.id == 0 ? "Add New Contact" : "Edit Contact"}</Modal.Title>
                <button type="button" className="close" onClick={hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <div className={loading ? "hidden" : ""}>
                    <label className="input-group" htmlFor="firstName">
                        <span className="label">First Name</span>
                        <input className="input" type="text" id="firstName" name="firstName" value={addContact.firstName} onChange={(e) => handleAddContactChange(e)} disabled={props.id > 0}></input>
                    </label>
                    <span className="label errors">{errors["firstName"]}</span>

                    <label className="input-group" htmlFor="lastName">
                        <span className="label">Last Name</span>
                        <input className="input" type="text" id="lastName" name="lastName" value={addContact.lastName} onChange={(e) => handleAddContactChange(e)}></input>
                    </label>
                    <span className="label errors">{errors["lastName"]}</span>

                    <label className="input-group" htmlFor="email">
                        <span className="label">Email</span>
                        <input className="input" type="text" id="email" name="email" value={addContact.email} onChange={(e) => handleAddContactChange(e)}></input>
                    </label>
                    <span className="label errors">{errors["email"]}</span>

                    <label className="input-group" htmlFor="phone">
                        <span className="label">Phone</span>
                        <input className="input" type="text" id="phone" name="phone" value={addContact.phone} onChange={(e) => handleAddContactChange(e)}></input>
                    </label>
                    <span className="label errors">{errors["phone"]}</span>

                    <label className="input-group" htmlFor="mobile">
                        <span className="label">Mobile</span>
                        <input className="input" type="text" id="mobile" name="mobile" value={addContact.mobile} onChange={(e) => handleAddContactChange(e)}></input>
                    </label>
                </div>
                <div className={loading ? "" : "hidden"}>
                    Loading...
                </div>
                
            </Modal.Body>

            <Modal.Footer>
                <button className="popup-button--yes" onClick={saveAddContact} disabled={saveDisabled}>Save</button>
            </Modal.Footer>
        </Modal>
    )

    let contents = props.showModal
        ? renderDetails
        : <div></div>;

    return (
        <div>
            {contents}
        </div>
    )

}

export default JobDetailsAddContactModalData;